import { pdf, PDFViewer } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useState } from "react";
import {
  McButton,
  McLoadingIndicator,
} from "@maersk-global/mds-react-wrapper/components-core";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { PostmortemServices } from "../../services/postmortemServices";
import { convertEpochToDateFormat } from "../../Utils/convertEpochToDateFormat";
import UserInfo from "../../context/UserAccessContext";
import SessionValidation from "../../context/SessionValidationContext";
import { ImageServices } from "../../services/imageServices";
import { ChainedFiveWhysService } from "../../services/chainedFiveWhysService";
import { postmortemApiEndpoint } from "../../config";
import ReportDoc from "./ReportDoc";
import { fetchMttrInsights } from "../../services/mttrInsightsService";
import { useQueryClient } from "react-query";
import { cachedRequest } from "../../Utils/cachedRequest";
import "./downloadReport.css";

function DownloadReport() {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { instance, accounts } = useMsal();
  const accountId = accounts[0]?.homeAccountId;
  const [postmortemData, setPostmortemData] = useState({});

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const incidentID  = params.get("incidentID");
  const postmortemID = params.get("postmortemID");

  const apiEndpoint = postmortemApiEndpoint;
  const { fetchPostmortemById } = PostmortemServices();
  const { graphData } = UserInfo();

  const [uploadedSummaryImages,              setUploadedSummaryImages]              = useState([]);
  const [uploadedPrecedingEventsImages,      setUploadedPrecedingEventsImages]      = useState([]);
  const [uploadedImpactImages,               setUploadedImpactImages]               = useState([]);
  const [uploadedDetectionAndResponseImages, setUploadedDetectionAndResponseImages] = useState([]);
  const [uploadedRootCauseImages,            setRootCauseUploadedImages]            = useState([]);
  const [rootCauseIdentificationUploadedImages, setRootCauseIdentificationUploadedImages] = useState([]);
  const [jiraUploadedImages,                 setJiraUploadedImages]                 = useState([]);
  const [ptaskUploadedImages,                setPtaskUploadedImages]                = useState([]);
  const [chainedFiveWhysData,                setChainedFiveWhysData]                = useState([]);
  const [mttrInsights,                       setMttrInsights]                       = useState(null);

  const { setSessionExpired } = SessionValidation();
  const { getImage } = ImageServices();
  const chainedService = ChainedFiveWhysService();
  const queryClient = useQueryClient();

  const fetchAndSetImages = useCallback(
    async (setter, tag) => {
      try {
        const images = await getImage(
          [],
          incidentID,
          tag,
          instance,
          accounts,
          setSessionExpired
        );
        setter(images);
      } catch (err) {
        console.error("Error fetching images:", err);
      }
    },
    [incidentID, instance, accountId, setSessionExpired, getImage]
  );

  useEffect(() => {
    if (!incidentID) return;
    fetchAndSetImages(setUploadedSummaryImages,              "summary");
    fetchAndSetImages(setUploadedPrecedingEventsImages,      "precedingEvents");
    fetchAndSetImages(setUploadedImpactImages,               "impact");
    fetchAndSetImages(setUploadedDetectionAndResponseImages, "detectionAndResponse");
    fetchAndSetImages(setRootCauseUploadedImages,            "rootCause");
    fetchAndSetImages(setRootCauseIdentificationUploadedImages, "rootCauseIdentification");
    fetchAndSetImages(setPtaskUploadedImages,                "ptask");
    fetchAndSetImages(setJiraUploadedImages,                 "jira");
  }, [incidentID]);

  useEffect(() => {
    if (!postmortemID || !accountId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchPostmortemById(
          apiEndpoint,
          `${postmortemID}?page_type=report`
        );
        setPostmortemData(data);
        
        // Fetch chained 5 whys data
        if (incidentID) {
          try {
            const chains = await chainedService.getChainedFiveWhysCached(queryClient, incidentID, instance, accounts);
            if (chains && chains.length > 0) {
              setChainedFiveWhysData(chains);
            }
          } catch (chainedError) {
            console.error("Failed to fetch chained 5 whys:", chainedError);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accountId, instance, postmortemID, incidentID]);

  // Fetch MTTD / MTTR from insights API (same source as the Report UI)
  useEffect(() => {
    if (!incidentID || !accountId) return;
    cachedRequest(queryClient, ["mttrInsights", incidentID], () => fetchMttrInsights(incidentID, instance, accounts))
      .then((data) => { if (data) setMttrInsights(data); })
      .catch(() => {});
  }, [incidentID, instance, accountId]);

  const docProps = {
    graphData,
    postmortemData,
    uploadedSummaryImages,
    uploadedPrecedingEventsImages,
    uploadedImpactImages,
    uploadedDetectionAndResponseImages,
    uploadedRootCauseImages,
    rootCauseIdentificationUploadedImages,
    jiraUploadedImages,
    ptaskUploadedImages,
    chainedFiveWhysData,
    mttdMinutes: mttrInsights?.ttd_seconds != null
      ? parseFloat(mttrInsights.ttd_seconds) / 60
      : postmortemData?.detectionResponse?.mttd,
    mttrMinutes: mttrInsights?.ttr_seconds != null
      ? parseFloat(mttrInsights.ttr_seconds) / 60
      : postmortemData?.detectionResponse?.mttrecover,
  };

  const saveFile = async () => {
    setDownloading(true);
    try {
      const blob = await pdf(<ReportDoc {...docProps} />).toBlob();
      const filename = [
        convertEpochToDateFormat(postmortemData?.createdOn),
        postmortemData?.incidentID,
        postmortemData?.short_description,
      ]
        .filter(Boolean)
        .join("-");
      saveAs(blob, `${filename}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="dr-loading">
        <McLoadingIndicator
          variant="spinner"
          fit="large"
          label="Generating report, please wait…"
        />
      </div>
    );
  }

  return (
    <div className="dr-page">
      {/* Toolbar */}
      <div className="dr-toolbar">
        <div className="dr-toolbar-info">
          <span className="dr-toolbar-id">{postmortemData?.customID}</span>
          {postmortemData?.incidentID && (
            <>
              <span className="dr-toolbar-sep">·</span>
              <span className="dr-toolbar-incident">{postmortemData.incidentID}</span>
            </>
          )}
          {postmortemData?.short_description && (
            <>
              <span className="dr-toolbar-sep">—</span>
              <span className="dr-toolbar-desc">{postmortemData.short_description}</span>
            </>
          )}
        </div>
        <McButton
          appearance="primary"
          icon="download"
          label={downloading ? "Downloading…" : "Download PDF"}
          disabled={downloading}
          onClick={saveFile}
        />
      </div>

      {/* PDF preview */}
      <div className="dr-viewer">
        <PDFViewer width="100%" height="100%" showToolbar={false}>
          <ReportDoc {...docProps} />
        </PDFViewer>
      </div>
    </div>
  );
}

export default DownloadReport;
